<template>
    <div class="page-container">
        <Pane />

        <div class="form-wrapper">
            <a-form :form="form" @submit="handleSubmit" class="custom-form">
                <!-- ================= 1. 基本信息 ================= -->
                <div class="section-block">
                    <div class="section-title"><span class="bar"></span>基本信息</div>

                    <a-row :gutter="24">
                        <a-col :span="12">
                            <a-form-item label="会议主题" class="required-label">
                                <a-input v-decorator="['title', { rules: [{ required: true, message: '请输入会议主题' }] }]" placeholder="请输入会议主题" />
                            </a-form-item>
                        </a-col>
                        <a-col :span="12">
                            <a-form-item label="会议级别" class="required-label">
                                <a-select v-decorator="['type', { rules: [{ required: true, message: '请选择' }] }]">
                                    <a-select-option v-for="item in meetingLevelList" :key="item.value" :value="item.value">
                                        {{ item.name }}
                                    </a-select-option>
                                </a-select>
                            </a-form-item>
                        </a-col>
                    </a-row>

                    <a-row :gutter="24">
                        <a-col :span="12">
                            <a-form-item label="会议日期" class="required-label">
                                <a-date-picker
                                        v-decorator="['date', { rules: [{ required: true, message: '请选择日期' }] }]"
                                        style="width: 100%"
                                        placeholder=""
                                        @change="handleDateChange"
                                />
                            </a-form-item>
                        </a-col>
                        <a-col :span="12">
                            <a-form-item label="会议室" class="required-label">
                                <a-select
                                        placeholder="请选择会议室"
                                        @change="handleRoomChange"
                                        v-decorator="['roomId', { rules: [{ required: true, message: '请选择会议室' }] }]">
                                    <a-select-option v-for="item in meetingRoomList" :key="item.id" :value="item.id">
                                        {{ item.name }}
                                    </a-select-option>
                                </a-select>
                            </a-form-item>
                        </a-col>
                    </a-row>
                </div>

                <!-- ================= 2. 时段选择 (时间轴) ================= -->
                <div class="section-block" v-if="timeList.length > 0">
                    <div class="section-title"><span class="bar"></span>时段选择 - {{ getCurrentRoomName() }} {{ getDateStr() }}</div>

                    <div class="timeline-container">
                        <!-- 时间刻度 -->
                        <div class="timeline-slots">
                            <div
                                    class="slot-item"
                                    v-for="(item, index) in timeList"
                                    :key="index"
                                    :class="getSlotClass(index)"
                                    @click="selectTime(item, index)"
                            >
                                <a-tooltip :title="getSlotTooltip(item)">
                                    <div class="slot-inner"></div>
                                </a-tooltip>
                            </div>
                        </div>

                        <!-- 底部小时刻度 -->
                        <div class="timeline-hours">
                            <div class="hour-label" v-for="(item, index) in timeList.filter(item => item.time.endsWith(':30'))" :key="index">
                                {{ item.time }}
                            </div>
                        </div>

                        <!-- 图例 -->
                        <div class="timeline-legend">
                            <span><span class="dot green"></span>可选</span>
                            <span><span class="dot red"></span>已预约</span>
                            <span><span class="dot blue"></span>已选</span>
                        </div>

                        <!-- 已选时段提示条 -->
                        <div class="selected-time-badge" v-if="isTimeSelected">
                            <span>已选时段：</span>
                            <span class="badge-box">
                {{ getSelectedTimeRange() }}
              </span>
                        </div>
                    </div>
                </div>

                <!-- ================= 3. 人员设置 ================= -->
                <div class="section-block">
                    <div class="section-title"><span class="bar"></span>人员设置</div>

                    <a-row :gutter="48">
                        <a-col :span="12">
                            <a-form-item class="required-label">
                                <span slot="label"><span class="required-star">*</span>主持人</span>
                                <employee-selector
                                        :single="true"
                                        @change="(arr) => { hostList = arr }"
                                        :value="hostList"
                                >
                                    <div class="emp-trigger" :class="{ 'has-val': hostList.length > 0 }">
                                        <template v-if="hostList.length > 0">
                                            <div class="emp-circle" :style="{ background: getColor(hostList[0].name) }">{{ hostList[0].name.charAt(0) }}</div>
                                            <span class="emp-name">{{ hostList[0].name }}</span>
                                        </template>
                                        <span v-else style="color: #bfbfbf;">请选择主持人</span>
                                    </div>
                                </employee-selector>
                            </a-form-item>
                        </a-col>
                        <a-col :span="12">
                            <a-form-item class="required-label">
                                <span slot="label"><span class="required-star">*</span>记录员</span>
                                <employee-selector
                                        :single="true"
                                        @change="(arr) => { recorderList = arr }"
                                        :value="recorderList"
                                >
                                    <div class="emp-trigger" :class="{ 'has-val': recorderList.length > 0 }">
                                        <template v-if="recorderList.length > 0">
                                            <div class="emp-circle" :style="{ background: getColor(recorderList[0].name) }">{{ recorderList[0].name.charAt(0) }}</div>
                                            <span class="emp-name">{{ recorderList[0].name }}</span>
                                        </template>
                                        <span v-else style="color: #bfbfbf;">请选择记录员</span>
                                    </div>
                                </employee-selector>
                            </a-form-item>
                        </a-col>
                    </a-row>

                    <div class="attendee-wrapper">
                        <div class="attendee-label">
                            <div><span class="required-star">*</span>参会人员</div>
                            <employee-selector
                                    :single="false"
                                    @change="(arr) => { attendeeList = arr }"
                                    :value="attendeeList"
                            >
                                <div class="add-attendee-btn">
                                    <a-icon type="user-add" /> 添加参会人员
                                </div>
                            </employee-selector>
                        </div>
                        <div class="attendee-content">
                            <div class="attendee-list">
                                <div class="attendee-tag" v-for="(item, index) in attendeeList" :key="index">
                                    <div class="emp-circle small" :style="{ background: getColor(item.name) }">{{ item.name.charAt(0) }}</div>
                                    <span>{{ item.name }}</span>
                                    <a-icon type="close" class="remove-icon" @click="removeAttendee(index)" />
                                </div>
                            </div>
                            <div class="attendee-tip">已选择 {{ attendeeList.length }} 名参会人（主持人和记录员已自动包含）</div>
                        </div>
                    </div>
                </div>

                <!-- ================= 4. 会议内容 ================= -->
                <div class="section-block">
                    <div class="section-title"><span class="bar"></span>会议内容</div>

                    <a-form-item label="会议内容（选填）">
                        <a-textarea
                                v-decorator="['content']"
                                placeholder="点击编辑会议议程内容..."
                                :auto-size="{ minRows: 4, maxRows: 8 }"
                        />
                    </a-form-item>
                </div>

                <!-- ================= 5. 附件上传 ================= -->
                <div class="section-block">
                    <div class="section-title"><span class="bar"></span>附件</div>

                    <div class="upload-area">
                        <div class="upload-trigger-row">
                            <a-button type="primary" icon="upload" @click="triggerFileInput">附件上传</a-button>
                            <input type="file" ref="fileInput" @change="handleFileChange" accept=".pdf,.docx,.doc,.jpg,.jpeg,.png" multiple style="display: none" />
                            <span class="upload-tip">支持 PDF、DOCX、JPG，单个文件不超过 20MB</span>
                        </div>

                        <div class="file-list" v-if="uploadFileList.length">
                            <div class="file-item" v-for="(file, index) in uploadFileList" :key="index">
                                <div class="file-info">
                                    <a-icon :type="fileIcon(file.name)" style="font-size: 18px; margin-right: 8px;" :style="{ color: fileIconColor(file.name) }" />
                                    <a v-if="file.completePath" :href="file.completePath" target="_blank" class="file-name">{{ file.name }}</a>
                                    <span v-else class="file-name">{{ file.name }}</span>
                                    <span class="file-size">{{ formatFileSize(file.size) }}</span>
                                </div>
                                <a-icon type="delete" class="delete-icon" style="color: #ff4d4f;" @click="removeFile(index)" />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- ================= 6. 底部按钮 ================= -->
                <div class="footer-actions">
                    <a-button class="btn-draft">保存草稿</a-button>
                    <a-button type="primary" html-type="submit" :loading="saveLoading" class="btn-publish">发布会议</a-button>
                </div>

            </a-form>
        </div>
    </div>
</template>

<script>
// ... 保持原本的 imports 不变
import { fetchList, addMeeting, fetchMeetingList } from "@/api/meeting";
import { uploadFile } from "@/api/upload";
import { mapGetters } from "vuex";
import { mapState } from "vuex";
import moment from "moment";
import EmployeeSelector from "@/components/employee-selector";

export default {
    name: "addMeeting",
    components: { EmployeeSelector },
    data() {
        return {
            form: this.$form.createForm(this),
            saveLoading: false,
            meetingRoomList: [],
            timeList: [],
            selectedStartIndex: null,
            selectedEndIndex: null,

            // 三个独立的人员列表
            hostList: [],
            recorderList: [],
            attendeeList: [],

            // 文件上传
            uploadFileList: [],
        };
    },
    computed: {
        ...mapGetters("setting", ["findDataDict"]),
        ...mapState("auth", ["user"]),
        meetingLevelList() {
            return this.findDataDict("meetingLevel") || [];
        },
        messageList() {
            return this.findDataDict("message");
        },
        isTimeSelected() {
            return typeof this.selectedStartIndex === 'number' && typeof this.selectedEndIndex === 'number';
        }
    },
    activated() {
        fetchList({ pageNum: 1, pageSize: 100 }).then((res) => {
            if (Array.isArray(res.list)) this.meetingRoomList = res.list;
        });
        const roomId = this.form.getFieldValue("roomId");
        const date = this.form.getFieldValue("date");
        if (roomId && date) this.getTimeList(roomId, date);
    },
    methods: {
        // --- 辅助方法 ---
        getColor(name) {
            // 简单随机颜色生成
            const colors = ['#1890ff', '#52c41a', '#eb2f96', '#faad14', '#722ed1','#d12e46', '#2ed1b3'];
            let hash = 0;
            for (let i = 0; i < name.length; i++) {
                hash = name.charCodeAt(i) + ((hash << 5) - hash);
            }
            return colors[Math.abs(hash) % colors.length];
        },
        getCurrentRoomName() {
            const id = this.form.getFieldValue('roomId');
            const room = this.meetingRoomList.find(r => r.id === id);
            return room ? room.name : '';
        },
        getDateStr() {
            const date = this.form.getFieldValue('date');
            return date ? date.format('YYYY-MM-DD') : '';
        },

        // --- 核心逻辑 ---
        handleRoomChange(id) { this.getTimeList(id, this.form.getFieldValue("date")); },
        handleDateChange(date) { this.getTimeList(this.form.getFieldValue("roomId"), date); },

        getTimeList(roomId, date) {
            if (!roomId || !date) return;
            const now = moment();
            if (!now.isSameOrBefore(date, "day")) {
                this.$message.error("最早选择今日！");
                return;
            }

            fetchMeetingList({
                pageNum: 1, pageSize: 1000, roomId,
                startTime: date.format("YYYY-MM-DD"), endTime: date.format("YYYY-MM-DD"),
            }).then((res) => {
                const time = [];
                for (let i = 7; i < 22; i++) {
                    time.push({ time: `${i < 10 ? "0" + i : i}:00` });
                    time.push({ time: `${i < 10 ? "0" + i : i}:30` });
                }

                if (Array.isArray(res.list)) {
                    const filterList = res.list.filter(
                        item => ["approved", "applying", "meeting", "end"].includes(item.state)
                    );
                    filterList.forEach((item) => {
                        const { startTime, endTime } = item;
                        if (startTime && endTime) {
                            const startArr = startTime.split(" ");
                            const endArr = endTime.split(" ");
                            if (startArr.length > 1 && endArr.length > 1) {
                                const time1 = startArr[1];
                                const time2 = endArr[1];
                                let startIndex, endIndex;
                                time.forEach((el, idx) => {
                                    if (el.time === time1) startIndex = idx;
                                    if (el.time === time2) endIndex = idx;
                                });
                                if (startIndex !== undefined && endIndex !== undefined) {
                                    for (let i = startIndex; i < endIndex; i++) {
                                        time[i].disabled = true;
                                    }
                                }
                            }
                        }
                    });
                }
                this.timeList = time;
                this.selectedStartIndex = null;
                this.selectedEndIndex = null;
            });
        },

        selectTime(item, index) {
            if (item.disabled) return;
            const S = this.selectedStartIndex, E = this.selectedEndIndex;

            // 新选择逻辑（兼容当前状态机）
            if (typeof S !== "number") {
                if (typeof E === "number") {
                    if (index < E && this.checkTime(index, E)) { this.selectedStartIndex = index; }
                    else if (index > E && this.checkTime(E, index)) { this.selectedStartIndex = E; this.selectedEndIndex = index; }
                    else if (index === E) { this.selectedEndIndex = null; }
                    else { this.$message.error("请选择正确的时间范围！"); return; }
                } else {
                    this.selectedStartIndex = index;
                    this.selectedEndIndex = index;
                }
            } else if (typeof E !== "number") {
                if (index < S && this.checkTime(index, S)) { this.selectedStartIndex = index; this.selectedEndIndex = S; }
                else if (index > S && this.checkTime(S, index)) { this.selectedEndIndex = index; }
                else if (index === S) { this.selectedStartIndex = null; }
                else { this.$message.error("请选择正确的时间范围！"); return; }
            } else {
                if (index === S && index === E) { this.selectedStartIndex = null; this.selectedEndIndex = null; }
                else if (index === S) { this.selectedStartIndex = null; }
                else if (index === E) { this.selectedEndIndex = null; }
                else if (index < (S + E) / 2) {
                    if (this.checkTime(index, E)) this.selectedStartIndex = index;
                    else { this.$message.error("请选择正确的时间范围！"); return; }
                } else {
                    if (this.checkTime(S, index)) this.selectedEndIndex = index;
                    else { this.$message.error("请选择正确的时间范围！"); return; }
                }
            }
        },

        checkTime(startIndex, endIndex) {
            for (let i = startIndex; i <= endIndex; i++) {
                if (this.timeList[i].disabled) return false;
            }
            return true;
        },

        getSlotClass(index) {
            const item = this.timeList[index];
            if (item.disabled) return 'disabled';
            if (this.isTimeSelected && index >= this.selectedStartIndex && index <= this.selectedEndIndex) return 'selected';
            return 'available';
        },

        // 修复了箭头函数语法错误，并添加了参数接收
        getSlotTooltip(item, index) {
            if (item.disabled) return "已预约";
            // 判断当前格子是否处于已选中的时间范围内
            if (this.isTimeSelected && index >= this.selectedStartIndex && index <= this.selectedEndIndex) return "已选";
            return "可选";
        },

        getSelectedTimeRange() {
            if (!this.isTimeSelected) return '';
            const start = this.timeList[this.selectedStartIndex].time;
            const end = this.selectedEndIndex === this.timeList.length - 1 ? "22:00" : this.timeList[this.selectedEndIndex + 1].time;
            return `${start} - ${end} (${this.calcDuration()}小时)`;
        },

        calcDuration() {
            if (!this.isTimeSelected) return 0;
            return (this.selectedEndIndex - this.selectedStartIndex + 1) / 2; // 30分钟一格
        },

        removeAttendee(index) {
            this.attendeeList.splice(index, 1);
        },

        // --- 文件上传 ---
        triggerFileInput() {
            this.$refs.fileInput.click();
        },
        async handleFileChange(e) {
            const files = Array.from(e.target.files || []);
            const newFiles = files.filter(file => !this.uploadFileList.some(f => f.name === file.name && f.size === file.size));
            if (!newFiles.length) { e.target.value = ''; return; }
            const data = new FormData();
            newFiles.forEach(file => data.append('files', file));
            try {
                const res = await uploadFile(data);
                if (Array.isArray(res)) {
                    this.uploadFileList = [...this.uploadFileList, ...res];
                }
            } catch (err) {
                console.error('上传文件失败', err);
                this.$message.error('文件上传失败');
            }
            e.target.value = '';
        },
        removeFile(index) {
            this.uploadFileList.splice(index, 1);
        },
        fileIcon(name) {
            const ext = name.split('.').pop().toLowerCase();
            if (['doc', 'docx'].includes(ext)) return 'file-word';
            if (['xls', 'xlsx'].includes(ext)) return 'file-excel';
            if (['ppt', 'pptx'].includes(ext)) return 'file-ppt';
            if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(ext)) return 'file-image';
            if (ext === 'pdf') return 'file-pdf';
            return 'file';
        },
        fileIconColor(name) {
            const ext = name.split('.').pop().toLowerCase();
            if (['doc', 'docx'].includes(ext)) return '#1890ff';
            if (['xls', 'xlsx'].includes(ext)) return '#52c41a';
            if (['ppt', 'pptx'].includes(ext)) return '#fa8c16';
            if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(ext)) return '#722ed1';
            if (ext === 'pdf') return '#f5222d';
            return '#8c8c8c';
        },
        formatFileSize(bytes) {
            if (!bytes) return '';
            const units = ['B', 'KB', 'MB', 'GB'];
            let i = 0;
            let size = bytes;
            while (size >= 1024 && i < units.length - 1) { size /= 1024; i++; }
            return size.toFixed(1) + ' ' + units[i];
        },

        // --- 提交 ---
        handleSubmit(e) {
            e.preventDefault();
            this.form.validateFields((err, values) => {
                if (!err) {
                    if (!this.isTimeSelected) {
                        this.$message.error("请选择会议时间！");
                        return;
                    }
                    if (this.hostList.length === 0) {
                        this.$message.error("请选择主持人！");
                        return;
                    }
                    if (this.recorderList.length === 0) {
                        this.$message.error("请选择记录员！");
                        return;
                    }

                    // 组装数据提交...
                    const date = values.date.format("YYYY-MM-DD");
                    let startTime = `${date} ${this.timeList[this.selectedStartIndex].time}`;
                    let endTime = this.selectedEndIndex === this.timeList.length - 1
                        ? `${date} 22:00`
                        : `${date} ${this.timeList[this.selectedEndIndex + 1].time}`;

                    // 整合参会人员（主持+记录+参会）
                    const hostAttendees = this.hostList.map(item => ({ userId: item.userId, userName: item.name, role: '主持人' }));
                    const recorderAttendees = this.recorderList.map(item => ({ userId: item.userId, userName: item.name, role: '记录人' }));
                    const normalAttendees = this.attendeeList.map(item => ({ userId: item.userId, userName: item.name, role: '参会人' }));
                    const allAttendees = [...hostAttendees, ...recorderAttendees, ...normalAttendees];
                    console.log("this.user::::",this.user)
                    this.$confirm({
                        title: "确认发布会议",
                        onOk: () => {
                            this.saveLoading = true;
                            const host = this.hostList[0] || {};
                            const recorder = this.recorderList[0] || {};
                            addMeeting({
                                ...values,
                                date: undefined,
                                startTime, endTime,
                                applicantName: this.user.userName,
                                hostId: host.userId,
                                hostName: host.name,
                                recorderId: recorder.userId,
                                recorderName: recorder.name,
                                attendeeList: allAttendees,
                                attachFileIds: this.uploadFileList.map(f => f.id).filter(Boolean).join(',')
                            }).then(() => {
                                this.$close(this.$route.path);
                            }).finally(() => {
                                this.saveLoading = false;
                            });
                        }
                    });
                }
            });
        }
    }
};
</script>

<style lang="less" scoped>
/* 基础容器 */
.page-container {
  background: #f5f7fa;
  min-height: 100vh;
}

.form-wrapper {
  background: #fff;
  margin: 16px 24px;
  padding: 24px 40px 24px 40px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.custom-form {
  ::v-deep .ant-form-item-label {
    font-weight: 500;
    color: #333;
  }
}

.required-star { color: #f5222d; margin-right: 4px; font-family: SimSun; }

/* 板块标题 */
.section-block {
  margin-bottom: 40px;
}
.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f1f1f;
  display: flex;
  align-items: center;
  margin-bottom: 24px;

  .bar {
    display: inline-block;
    width: 4px;
    height: 16px;
    background: #1890ff;
    border-radius: 2px;
    margin-right: 8px;
  }
}



/* ===== 时间轴部分 ===== */
.timeline-container {
  background: #f9fafc;
  padding: 20px 16px 16px;
  border-radius: 6px;
  border: 1px solid #ebeef5;
  margin-top: -10px;

  .timeline-slots {
    display: flex;
    flex-wrap: wrap;
    background: #fff;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    overflow: hidden;

    .slot-item {
      flex: 1;
      min-width: 10px;
      height: 28px;
      border-right: 1px solid #f0f0f0;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;

      .slot-inner {
        width: 100%;
        height: 100%;
      }

      &.available .slot-inner { background: #52c41a; } /* 可选：绿 */
      &.disabled .slot-inner { background: #ff4d4f; cursor: not-allowed; } /* 已预约：红 */
      &.selected .slot-inner { background: #1890ff; } /* 已选：蓝 */

      &:last-child { border-right: none; }
    }
  }

  .timeline-hours {
    display: flex;
    margin-top: 4px;
    .hour-label {
      flex: 1;
      min-width: 10px;
      text-align: center;
      font-size: 12px;
      color: #8c8c8c;
    }
  }

  .timeline-legend {
    display: flex;
    justify-content: center;
    gap: 20px;
    margin-top: 16px;
    font-size: 13px;
    color: #595959;

    .dot {
      display: inline-block;
      width: 12px;
      height: 12px;
      border-radius: 2px;
      margin-right: 6px;
      vertical-align: middle;
      &.green { background: #52c41a; }
      &.red { background: #ff4d4f; }
      &.blue { background: #1890ff; }
    }
  }

  .selected-time-badge {
    margin-top: 12px;
    text-align: center;
    font-size: 13px;
    color: #333;

    .badge-box {
      display: inline-block;
      background: #e6f7ff;
      color: #1890ff;
      border: 1px solid #91d5ff;
      padding: 2px 12px;
      border-radius: 4px;
      margin-left: 6px;
    }
  }
}

/* ===== 人员组件 ===== */
.emp-trigger {
  height: 32px;
  border: 1px solid #d9d9d9;
  border-radius: 2px;
  padding: 0 11px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s;
  background: #fff;

  &:hover { border-color: #1890ff; }
  &.has-val { background: #f0f5ff; border-color: #adc6ff; }

  .emp-circle {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 12px;
    margin-right: 8px;
  }
  .emp-name { color: #1890ff; }
}

.attendee-wrapper {
  margin-bottom: 24px;

  .attendee-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-weight: 500;
    color: #333;
    margin-bottom: 8px;
  }
  .required-star { color: #f5222d; margin-right: 4px; font-family: SimSun; }
  .add-attendee-btn {
    color: #1890ff;
    cursor: pointer;
    padding: 0 8px;
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    &:hover { background: #e6f7ff; border-radius: 4px; }
  }

  .attendee-content {
    background: #fafafa;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    padding: 8px 12px;
    min-height: 40px;

    .attendee-list {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px;

      .attendee-tag {
        display: flex;
        align-items: center;
        background: #fff;
        border: 1px solid #d9d9d9;
        border-radius: 16px;
        padding: 0 8px 0 4px;
        height: 28px;
        font-size: 13px;

        .emp-circle.small {
          width: 20px; height: 20px; font-size: 10px; margin-right: 4px;border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #fff;
            font-size: 12px;
        }
        .remove-icon {
          font-size: 10px;
          margin-left: 4px;
          color: #999;
          cursor: pointer;
          &:hover { color: #f5222d; }
        }
      }

    }

    .attendee-tip {
      margin-top: 8px;
      font-size: 12px;
      color: #8c8c8c;
    }
  }
}

/* ===== 附件上传 ===== */
.upload-area {
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 16px;

  .upload-trigger-row {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
    .upload-tip { color: #8c8c8c; font-size: 12px; }
  }

  .file-list {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    padding: 8px 12px;

    .file-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      .file-info {
        display: flex;
        align-items: center;
        .file-name { font-weight: 500; margin-right: 12px; }
        .file-size { color: #8c8c8c; font-size: 12px; }
      }
      .delete-icon {
        cursor: pointer;
        &:hover { opacity: 0.7; }
      }
    }
  }
}

/* ===== 底部按钮 ===== */
.footer-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 24px;
  border-top: 1px solid #f0f0f0;
  gap: 12px;

  .btn-draft {
    background: #f5f5f5;
    border-color: #d9d9d9;
    color: #595959;
    &:hover { background: #e6e6e6; }
  }

  .btn-publish {
    background: #1890ff;
    border-color: #1890ff;
  }
}
</style>